import { Group, MultiSelect, Stack, TextInput, Tooltip } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconArrowDown, IconArrowUp, IconCheck, IconPlus, IconTrash } from '@tabler/icons-react';
import { useApi } from 'api/api-context';
import Button from 'components/Button';
import ActionIcon from 'components/ActionIcon';
import FormInputGroup from 'components/forms/FormInputGroup';
import FormWrapper from 'components/forms/FormWrapper';
import createValidator from 'components/forms/validators/create-validator';
import required from 'components/forms/validators/rules/rule-required';
import panic from 'errors/panic';
import useLoadingAction from 'hooks/use-loading-action';
import { ReactNode, useEffect, useState } from 'react';
import { useDirtyFormHandler } from 'hooks/use-dirty-form-handler';

export interface HierarchyFormLevel {
  hierarchyLevelId?: number;
  label: string;
}

export interface HierarchyFormData {
  hierarchyName: string;
  levels: HierarchyFormLevel[];
  organizationIds: string[];
}

interface HierarchyFormProps {
  initialValues?: HierarchyFormData;
  structureFrozen?: boolean;
  onSubmit?: (values: HierarchyFormData) => Promise<unknown>;
  primaryButtonText?: string;
  primaryIcon?: ReactNode;
}

export const hierarchyFormInitialValues: HierarchyFormData = {
  hierarchyName: '',
  levels: [{ label: 'Stredisko' }],
  organizationIds: [],
};

/** Form for hierarchy identity, ordered levels and exact organization assignment. */
export default function HierarchyForm({
  initialValues = hierarchyFormInitialValues,
  structureFrozen = false,
  onSubmit = async () => undefined,
  primaryButtonText = 'Uložiť',
  primaryIcon = <IconCheck stroke="1.5" size={24} />,
}: HierarchyFormProps) {
  const { getAction } = useApi();
  const [{ loading }, submit] = useLoadingAction(onSubmit);
  const [organizations, setOrganizations] = useState<{ value: string; label: string }[]>([]);
  const form = useForm<HierarchyFormData>({
    initialValues,
    validate: {
      hierarchyName: createValidator([required]),
      levels: (levels) =>
        levels.length === 0 || levels.some(({ label }) => !label.trim()) ? 'Vyplňte aspoň jednu úroveň' : null,
    },
  });
  useDirtyFormHandler(form);

  useEffect(() => {
    getAction('OrganizationList')()
      .then((items) =>
        setOrganizations(
          items
            .filter(({ status }) => status)
            .map(({ organizationId, organizationName }) => ({ value: String(organizationId), label: organizationName }))
        )
      )
      .catch(panic);
  }, [getAction]);

  /** Moves an unused hierarchy level by one position. */
  const moveLevel = (index: number, offset: number) => {
    const next = [...form.values.levels];
    [next[index], next[index + offset]] = [next[index + offset], next[index]];
    form.setFieldValue('levels', next);
  };

  return (
    <FormWrapper
      onSubmit={form.onSubmit(submit)}
      loading={loading}
      primaryButtonText={primaryButtonText}
      primaryIcon={primaryIcon}
      skipSecondaryButtonConfirm={!form.isDirty()}
    >
      <FormInputGroup groupTitle="Hierarchia">
        <TextInput label="Názov" size="lg" {...form.getInputProps('hierarchyName')} />
      </FormInputGroup>

      <FormInputGroup groupTitle="Úrovne">
        <Stack spacing="sm" mt="sm">
          {form.values.levels.map((level, index) => (
            <Group key={level.hierarchyLevelId ?? `new-${index}`} noWrap align="flex-start">
              <TextInput
                label={`Úroveň ${index + 1}`}
                size="lg"
                value={level.label}
                error={form.errors.levels}
                onChange={(event) => form.setFieldValue(`levels.${index}.label`, event.currentTarget.value)}
                sx={{ flex: 1 }}
              />
              {!structureFrozen && (
                <Group spacing={4} mt={26} noWrap>
                  <Tooltip label="Posunúť vyššie">
                    <ActionIcon disabled={index === 0} onClick={() => moveLevel(index, -1)}>
                      <IconArrowUp size={18} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Posunúť nižšie">
                    <ActionIcon disabled={index === form.values.levels.length - 1} onClick={() => moveLevel(index, 1)}>
                      <IconArrowDown size={18} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Odstrániť úroveň">
                    <ActionIcon
                      color="red"
                      disabled={form.values.levels.length === 1}
                      onClick={() => form.removeListItem('levels', index)}
                    >
                      <IconTrash size={18} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              )}
            </Group>
          ))}
          {!structureFrozen && (
            <Button
              type="button"
              variant="subtle"
              leftIcon={<IconPlus size={18} />}
              onClick={() => form.insertListItem('levels', { label: '' })}
              sx={{ alignSelf: 'flex-start' }}
            >
              Pridať úroveň
            </Button>
          )}
        </Stack>
      </FormInputGroup>

      <FormInputGroup groupTitle="Priame priradenie organizácií">
        <MultiSelect
          searchable
          clearable
          size="lg"
          label="Organizácie"
          data={organizations}
          {...form.getInputProps('organizationIds')}
        />
      </FormInputGroup>
    </FormWrapper>
  );
}
